export type EmploymentContract = {
  id: string;
  contractType: string;
  contractStatus: string;
  signedOn?: string;
  weeklyHours?: string;
  effectiveFrom: string;
  effectiveTo?: string;
};
export type ContractValues = {
  contractType: string;
  signedOn: string;
  endDate: string;
  weeklyHours: string;
};
export type ContractChangeInput = {
  worker: { id: string };
  effectiveDate: string;
  reason: string;
  contract: ContractValues;
  expectedVersion?: number;
};
export type ContractChange = {
  businessProcessId?: string;
  workflowId?: string;
  id: string;
  effectiveDate: string;
  status: string;
  version: number;
  createdAt?: string;
  approvedAt?: string;
  effects?: { id: string; descriptor?: string }[];
};
export type ContractChangeDetail = ContractChange & { contract: ContractValues; reason: string };
export type ContractTransition = 'submit' | 'approve' | 'reject' | 'cancel';
export interface EmploymentContractApi {
  getContracts(workerId: string, asOf: string): Promise<{ contracts: EmploymentContract[] }>;
  listChanges(workerId: string): Promise<ContractChange[]>;
  getChange(changeId: string): Promise<ContractChangeDetail>;
  create(input: ContractChangeInput, key: string): Promise<ContractChange>;
  update(changeId: string, input: ContractChangeInput, key: string): Promise<ContractChange>;
  transition(changeId: string, action: ContractTransition, version: number, key: string): Promise<ContractChange>;
  cancelPending(eventId: string, key: string): Promise<unknown>;
  isDefinitiveFailure(error: unknown): boolean;
}
export const contractTypeLabels: Record<string, string> = {
  PERMANENT: '무기계약', FIXED_TERM: '기간제 계약', CONTINGENT: '외부 인력 계약', INTERN: '인턴 계약', OTHER: '기타 계약',
};
export const contractStatusLabels: Record<string, string> = {
  DRAFT: '초안', IN_PROGRESS: '승인 대기', APPROVED: '승인 완료', CANCELLED: '취소', REJECTED: '반려',
};
export function contractFormError(input: ContractChangeInput): string {
  if (!input.effectiveDate || !/^\d{4}-\d{2}-\d{2}$/.test(input.effectiveDate)) return '시행일을 입력해 주세요.';
  if (!contractTypeLabels[input.contract.contractType]) return '계약 유형을 선택해 주세요.';
  if (!input.reason.trim()) return '변경 사유를 입력해 주세요.';
  if ([...input.reason.trim()].length > 4000) return '변경 사유는 4,000자까지 입력할 수 있습니다.';
  if (input.contract.endDate && input.contract.endDate <= input.effectiveDate) return '계약 종료일은 시행일보다 뒤여야 합니다.';
  const hours = Number(input.contract.weeklyHours);
  if (input.contract.weeklyHours && (!Number.isFinite(hours) || hours <= 0 || hours > 168)) return '주당 근로시간은 0보다 크고 168 이하로 입력해 주세요.';
  return '';
}
export function contractDateToday(): string {
  const parts = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date());
  const part = (type: string) => parts.find((item) => item.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
}
export function contractValues(contract?: EmploymentContract): ContractValues {
  return { contractType: contract?.contractType ?? 'PERMANENT', signedOn: contract?.signedOn ?? '', endDate: contract?.effectiveTo ?? '', weeklyHours: contract?.weeklyHours ?? '' };
}

/** 승인 문서의 이력을 표시하며 오늘의 계약이 같은 값이라고 추정하지 않습니다. */
export function contractExecutionLabel(change: ContractChange, asOf: string): string {
  if (change.status === 'CANCELLED') return '취소됨 · 미반영';
  if (change.status === 'REJECTED') return '반려됨 · 미반영';
  if (change.status === 'APPROVED') {
    if (!change.approvedAt || !change.effects?.length) return '승인 완료 · 반영 확인 필요';
    return change.effectiveDate > asOf ? '승인 완료 · 적용 예정' : '승인 완료 · 적용 완료';
  }
  return `${contractStatusLabels[change.status] ?? change.status} · 미반영`;
}

/** 시행일이 바뀌면 직접 수정한 필드만 유지하고 나머지는 새 기준 계약을 따른다. */
export function rebaseContractValues(values: ContractValues, baseline: ContractValues, touched: ReadonlySet<keyof ContractValues>): ContractValues {
  return Object.fromEntries((Object.keys(baseline) as (keyof ContractValues)[]).map(key => [key, touched.has(key) ? values[key] : baseline[key]])) as ContractValues;
}
